/* eslint-disable no-console */
/* eslint-disable max-lines */
import { useUserInfo } from '@react/User/useUserInfo'
import { useEffect, useState } from 'react'

interface ProductMapping {
  productCategory: string
  mappedValue: string
  hepAccessGroup: string
  'product-tags': string[]
  'data-path': string
}

interface EventItem {
  title?: string
  tags?: string[]
  powerportalTags?: string[]
  [key: string]: unknown
}

interface ResponseData {
  items?: EventItem[]
  [key: string]: unknown
}

interface PowerPortalNewsProps {
  responseData: ResponseData | null
  onDataProcessed: (processedData: ResponseData | null) => void
}

export const usePowerPortalWebinar = ({ responseData, onDataProcessed }: PowerPortalNewsProps) => {
  // Get current user information
  const { userDetails } = useUserInfo()
  const [productMapping, setProductMapping] = useState<Record<string, ProductMapping> | null>(null)
  const [matchedCategories, setMatchedCategories] = useState<string[]>([])
  // Fetch product mapping
  useEffect(() => {
    const fetchProductMapping = async () => {
      try {
        const apiUrl = `/bin/pg/powerportal/personalization-mapping.json`
        const response = await fetch(apiUrl, {
          credentials: 'same-origin'
        })
  
        if (!response.ok) {
          throw new Error(`API request failed with status`)
        }
        const data = await response.json()
        setProductMapping(data)

      } catch (error) {
        console.error('Error details:', error instanceof Error ? error.message : String(error))
      }
    }
    fetchProductMapping()
  }, []) 

  // Match partnerProgramPreference with product mapping
  useEffect(() => {
    const productPreference =  userDetails?.powerPortalProfile?.samlAttributes?.partnerProgramPreference as string | undefined;
    
    if (!productPreference) {
      return
    }
    
    if (!productMapping) {
      return
    }
    
    //STEP 2: Processing partnerProgramPreference:", productPreference
    
    // Split the comma-separated preference codes
    const preferenceCodes = productPreference.split(',').map(code => code.trim())    
    
    // Match codes with product mapping
    const matchedProducts = Object.entries(productMapping)
      .filter(([code, _product]) => preferenceCodes.includes(code))
      .map(([code, product]) => ({ code, ...product }))
  
    
    if (matchedProducts.length > 0) {
      const categories: string[] = []
      matchedProducts.forEach((product) => {
        if (product.productCategory && !categories.includes(product.productCategory)) {
          categories.push(product.productCategory)
        }
      })
      setMatchedCategories(categories)
    } else {
      setMatchedCategories([])
    }
  }, [userDetails?.powerPortalProfile?.samlAttributes?.partnerProgramPreference, productMapping]) 

  // Process data (only once when responseData changes)
  useEffect(() => {
    const isPowerPortal = document.body.classList.contains('cmp-powerportal-main')
    if (!isPowerPortal) {
      // Not in PowerPortal, return original data as-is (no processing)
      onDataProcessed(responseData)
      return
    }
    const dataToProcess: ResponseData = responseData
    // Sort items based on matched categories
    if (matchedCategories.length > 0 && dataToProcess && dataToProcess.items) {
      
      const sortedItems = [...dataToProcess.items].sort((a: EventItem, b: EventItem) => {
        // Use powerportalTags instead of tags
        const aTags = a.powerportalTags || a.tags || []
        const bTags = b.powerportalTags || b.tags || []
      
        // Check if item A has any tag that includes a matched category (partial match)
        const aHasMatch = aTags.some((tag: string) => {
          return matchedCategories.some(category => {
            return tag.includes(category)
          })
        })
        
        // Check if item B has any tag that includes a matched category (partial match)
        const bHasMatch = bTags.some((tag: string) => {
          return matchedCategories.some(category => {
            return tag.includes(category)
          })
        })
        
        // If A matches but B doesn't, A comes first
        if (aHasMatch && !bHasMatch) { 
          return -1
        }
        // If B matches but A doesn't, B comes first
        if (bHasMatch && !aHasMatch) {
          return 1
        }

        return 0
      })      
      sortedItems.forEach((item: EventItem) => {
        const itemTags = item.powerportalTags || item.tags || []
        const hasMatch = itemTags.some((tag: string) => 
          matchedCategories.some(category => tag.includes(category))
        )
        if (hasMatch) {
          itemTags.filter((tag: string) => 
            matchedCategories.some(category => tag.includes(category))
          )
        }
      })

      const processedData: ResponseData = {
        ...dataToProcess,
        items: sortedItems
      }

      onDataProcessed(processedData)
    } else {
      onDataProcessed(dataToProcess)
    }

  }, [responseData, matchedCategories, onDataProcessed])
}
